/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving and saving a batch.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialBatchState {
    batch: Dtos.Batch | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
    saveState: IRequestState<Dtos.ResponseStatus>;
};

export interface IBatchState {
    batch: IInitialBatchState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const BatchInitialState: IInitialBatchState = {
    batch: null,
    loadState: {
        state: RequestState.None
    },
    saveState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useBatchSelector: TypedUseSelectorHook<IBatchState> = useSelector;

export const BatchSelectors = {
    selectBatch: (state: IBatchState) => state.batch.batch,
    selectLoadState: (state: IBatchState) => state.batch.loadState,
    selectSaveState: (state: IBatchState) => state.batch.saveState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class BatchReducer extends ImmerReducer<IInitialBatchState> {
    public loadById(id: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(batch?: Dtos.Batch) {
        this.draftState.batch = batch ? batch : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.batch = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public save(form: Dtos.Batch) {
        this.draftState.saveState = {
            state: RequestState.Pending
        };
    }

    public saveSuccess(batch?: Dtos.Batch) {
        this.draftState.batch = batch ? batch : null;
        this.draftState.saveState = {
            state: RequestState.Success
        };
    }

    public saveFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.batch = null;
        this.draftState.saveState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...BatchInitialState };
    }
};

export const BatchActions = createActionCreators(BatchReducer);
const reducer = createReducerFunction(BatchReducer, BatchInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const BatchApi = {
    loadById: (id: number) => {
        return client.get(new Dtos.GetBatchById({
            id
        }));
    },
    save: (batch?: Dtos.Batch) => {
        return client.post(new Dtos.SetBatch({
            batch
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const BatchLogic = {
    loadById: createLogic<IBatchState, {}, undefined, string, ReturnType<typeof BatchActions.loadById>>({
        type: BatchActions.loadById.type,
        process: async ({ action }, dispatch, done) => {
            const id = action.payload;

            try {
                const response = await BatchApi.loadById(id);

                dispatch(BatchActions.loadSuccess(
                    response.batch
                ));
            }
            catch (error: any) {
                dispatch(BatchActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    save: createLogic<IBatchState, {}, undefined, string, ReturnType<typeof BatchActions.save>>({
        type: BatchActions.save.type,
        process: async ({ action }, dispatch, done) => {
            const batch = action.payload;

            try {
                const response = await BatchApi.save(batch);

                dispatch(BatchActions.saveSuccess(
                    response.batch
                ));
            }
            catch (error: any) {
                dispatch(BatchActions.saveFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    BatchLogic.loadById,
    BatchLogic.save
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useBatch = (): [Dtos.Batch | null, IRequestState<Dtos.ResponseStatus>, IRequestState<Dtos.ResponseStatus>, typeof BatchActions] => {
    const batch = useSelector(BatchSelectors.selectBatch);
    const loadState = useSelector(BatchSelectors.selectLoadState);
    const saveState = useSelector(BatchSelectors.selectSaveState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof BatchActions>(() => {
        return bindActionCreators(BatchActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(BatchActions, dispatch));
    }, [dispatch, setActions]);

    return [
        batch,
        loadState,
        saveState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('batch', reducer as any, logics as any);